import styled from '@emotion/styled';
import { layout, palette, radius, shadow, space, spacing, status, typography } from '../../../../style-utils/theme';
import { up } from '../../../../style-utils/media-queries';

/**
 * The shared parts of the stay form (plan S4, S4.9): the New stay page draws
 * its sections as cards; the Edit stay modal draws the same sections flat,
 * divided by a hairline, inside the modal's own card.
 */

export const FormSection = styled.section<{ $flat?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${({ $flat }) =>
    $flat
      ? `
  padding: ${spacing.md} 0 0;
  margin-top: ${spacing.md};
  border-top: 1px solid ${palette.line};
  &:first-of-type {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
  }
`
      : `
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  ${up('sm')} {
    padding: ${spacing.md};
  }
`}
`;

export const SectionTitle = styled.h3`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

/* A label with a "changed" tag beside it (after a stale reload). */
export const LabelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* The read-only twin of a field: its label and the value as text. */
export const StaticLabel = styled.span`
  display: block;
  margin-bottom: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
`;

export const StaticValue = styled.p`
  display: flex;
  align-items: center;
  min-height: ${layout.fieldHeight};
  margin: 0;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  color: ${palette.ink};
  overflow-wrap: anywhere;
  min-width: 0;
`;

/* role="alert" refusals (a clash, a stale stay, a server refusal). */
export const AlertBanner = styled.div`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.sm};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.sm};
  background: ${status.alert.bg};
  color: ${status.alert.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  overflow-wrap: anywhere;
`;

/* role="status" notices that are information, not a problem. */
export const NeutralNotice = styled.p`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${status.neutral.bg};
  color: ${status.neutral.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  overflow-wrap: anywhere;
`;

/* The live percentages / breakdown under the amounts. */
export const MoneyLine = styled.p`
  min-height: ${spacing.md};
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.normal};
  color: ${palette.inkSoft};
  overflow-wrap: anywhere;
`;

export const ControlStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-width: 0;
  margin-bottom: ${spacing.sm};
`;

/* A date input with its two nudges: wraps under the input on a phone. */
export const NudgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

export const NudgeInput = styled.div`
  flex: 1 1 ${space(20)};
  min-width: 0;
`;

export const FormFieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;
